/** User preference for Bluetooth print/connect routing (zeettu-fe localStorage). */

export type BluetoothProviderPreference =
  | 'payzhe-btp'
  | 'browser-fallback'
  | 'browser-always'
  | 'ask';

const KEYS = {
  provider: 'zeettu.bluetooth.provider',
  autoReconnect: 'zeettu.payzheBtp.autoReconnect',
  requireService: 'zeettu.payzheBtp.requireService',
  askSession: 'zeettu.bluetooth.askSessionChoice',
  clientId: 'zeettu.payzheBtp.clientId',
} as const;

const DEFAULT_PROVIDER: BluetoothProviderPreference = 'payzhe-btp';

function readStorage(key: string): string | null {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return null;
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalStorageSet(key, value);
  } catch {
    /* ignore */
  }
}

function globalStorageSet(key: string, value: string): void {
  globalThis.localStorage.setItem(key, value);
}

function readBoolean(key: string, defaultValue: boolean): boolean {
  const raw = readStorage(key);
  if (raw === null) return defaultValue;
  return raw === 'true' || raw === '1';
}

export function getBluetoothProviderPreference(): BluetoothProviderPreference {
  const raw = readStorage(KEYS.provider);
  if (
    raw === 'payzhe-btp' ||
    raw === 'browser-fallback' ||
    raw === 'browser-always' ||
    raw === 'ask'
  ) {
    return raw;
  }
  const envOverride = (import.meta.env.VITE_BLUETOOTH_PROVIDER as string | undefined)?.trim();
  if (
    envOverride === 'browser' ||
    envOverride === 'browser-always'
  ) {
    return 'browser-always';
  }
  if (envOverride === 'payzhe-btp') {
    return 'payzhe-btp';
  }
  return DEFAULT_PROVIDER;
}

export function setBluetoothProviderPreference(value: BluetoothProviderPreference): void {
  writeStorage(KEYS.provider, value);
}

export function getPayzheBtpAutoReconnect(): boolean {
  return readBoolean(KEYS.autoReconnect, true);
}

export function setPayzheBtpAutoReconnect(value: boolean): void {
  writeStorage(KEYS.autoReconnect, value ? 'true' : 'false');
}

export function getPayzheBtpRequireService(): boolean {
  return readBoolean(KEYS.requireService, true);
}

export function setPayzheBtpRequireService(value: boolean): void {
  writeStorage(KEYS.requireService, value ? 'true' : 'false');
}

export function getAskSessionChoice(): BluetoothProviderPreference | null {
  const raw = readStorage(KEYS.askSession);
  if (
    raw === 'payzhe-btp' ||
    raw === 'browser-fallback' ||
    raw === 'browser-always'
  ) {
    return raw;
  }
  return null;
}

export function setAskSessionChoice(value: BluetoothProviderPreference | null): void {
  if (value === null || value === 'ask') {
    try {
      globalThis.localStorage.removeItem(KEYS.askSession);
    } catch {
      /* ignore */
    }
    return;
  }
  writeStorage(KEYS.askSession, value);
}

export function getOrCreateZeettuClientId(): string {
  const existing = readStorage(KEYS.clientId);
  if (existing && existing.length >= 8) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `zeettu-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  writeStorage(KEYS.clientId, id);
  return id;
}
